import {getCurrentUserLogin} from '@github-ui/client-env'
import {RepoPullsDashboardDeferredResponseSchema} from '@github-ui/dotcom-schema/types/pull_requests/v1/repo_pulls_dashboard'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useSuspenseRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useQuery} from '@github-ui/react-query'
import {useLocation, useSearchParams} from '@github-ui/react-router'
import {useNavigate} from '@github-ui/use-navigate'
import {useMemo} from 'react'

import {repoPullsDashboardContentRoute, repoPullsQueryResolver} from '../routes/repo-pulls-dashboard-content-route'
import {
  getRepoPullsAnalyticsView,
  getRepoPullsSidebarLinks,
  getSelectedRepoPullsSidebarLink,
} from '../routes/repo-pulls-sidebar-links'
import {
  REPO_PULLS_DEFERRED_PERSISTER_CONFIG,
  repoPullsDeferredQueryFn,
  repoPullsToListItems,
} from '../helpers/repo-pulls-dashboard-proto'

const DEFERRED_STALE_TIME = 30_000

function useRepoPullsRouteState() {
  const location = useLocation()
  const {owner, repo} = useRouteParams(repoPullsDashboardContentRoute)
  const [searchParams] = useSearchParams()

  const fetchedQuery = repoPullsQueryResolver({pathname: location.pathname, searchParams})
  const currentPage = Number(searchParams.get('page') || 1)
  const milestone = searchParams.get('milestone') ?? ''
  const pathname = repoPullsDashboardContentRoute.generatePath({owner, repo})

  function buildSearch(q: string, page = 1) {
    const nextSearchParams = new URLSearchParams()
    const normalizedQuery = q.trim()
    if (normalizedQuery) nextSearchParams.set('q', normalizedQuery)
    if (page > 1) nextSearchParams.set('page', String(page))
    if (milestone) nextSearchParams.set('milestone', milestone)

    return nextSearchParams.toString()
  }

  function hrefForQuery(q: string, page = 1) {
    const search = buildSearch(q, page)
    return `${pathname}${search ? `?${search}` : ''}`
  }

  return {currentPage, fetchedQuery, hrefForQuery, location, milestone, owner, pathname, repo}
}

export function useRepoPullsSearch() {
  const {currentPage, fetchedQuery, hrefForQuery, location, milestone, owner, repo} = useRepoPullsRouteState()
  const navigate = useNavigate()
  const currentUserLogin = getCurrentUserLogin()
  const analyticsView = getRepoPullsAnalyticsView(fetchedQuery, location.pathname, {currentUserLogin, owner, repo})

  function navigateToResults({q, page = 1}: {q: string; page?: number}) {
    navigate(hrefForQuery(q, page))
  }

  function getPageHref(page: number) {
    return hrefForQuery(fetchedQuery, page)
  }

  return {
    analyticsView,
    currentPage,
    fetchedQuery,
    getPageHref,
    hrefForQuery,
    milestone,
    navigateToResults,
    owner,
    repo,
  }
}

export function useRepoPullsSidebar() {
  const {fetchedQuery, hrefForQuery, location, owner, repo} = useRepoPullsRouteState()
  const currentUserLogin = getCurrentUserLogin()
  const sidebarLinksParams = {currentUserLogin, owner, repo}
  const sidebarLinks = getRepoPullsSidebarLinks(sidebarLinksParams)
  const selectedLink = getSelectedRepoPullsSidebarLink(fetchedQuery, location.pathname, sidebarLinksParams)

  function generateLinkPath(path: string | undefined, query?: string) {
    if (path !== undefined) return path
    return hrefForQuery(query ?? fetchedQuery)
  }

  return {generateLinkPath, selectedLink, sidebarLinks}
}

export function useRepoPullsDashboardData() {
  const mainQuery = useSuspenseRouteQuery(repoPullsDashboardContentRoute, 'mainQuery')
  const mainResults = mainQuery.data.results
  const location = useLocation()
  const {owner, repo} = useRouteParams(repoPullsDashboardContentRoute)
  const [searchParams] = useSearchParams()
  const deferredQueryDeps = useMemo(() => {
    const nextSearchParams: Array<[string, string]> = [
      ['q', repoPullsQueryResolver({pathname: location.pathname, searchParams})],
      ['page', searchParams.get('page') || '1'],
    ]
    const milestone = searchParams.get('milestone')
    if (milestone) nextSearchParams.push(['milestone', milestone])
    for (const pull of mainResults) nextSearchParams.push(['pr_ids[]', String(pull.id)])

    return {
      pathname: `/${owner}/${repo}/pulls/_deferred`,
      searchParams: nextSearchParams,
    }
  }, [location.pathname, mainResults, owner, repo, searchParams])
  const deferredQueryKey = useMemo(
    () => ['pull-requests', 'repo-pulls-dashboard', 'deferred', deferredQueryDeps],
    [deferredQueryDeps],
  )
  const deferredQuery = useQuery({
    queryKey: deferredQueryKey,
    schema: RepoPullsDashboardDeferredResponseSchema,
    queryFn: ({signal}) =>
      repoPullsDeferredQueryFn({
        routeId: 'repoPullsDashboardDeferredRoute',
        queryName: 'deferred',
        queryDeps: {...deferredQueryDeps, init: {signal}},
      }),
    enabled: mainResults.length > 0,
    staleTime: DEFERRED_STALE_TIME,
    persister: REPO_PULLS_DEFERRED_PERSISTER_CONFIG,
  })
  const deferredResults = deferredQuery.data?.error ? undefined : deferredQuery.data?.results
  const pulls = useMemo(() => repoPullsToListItems(mainResults, deferredResults), [deferredResults, mainResults])
  const stateCounts =
    mainQuery.data.openCount === undefined || mainQuery.data.closedCount === undefined
      ? undefined
      : {open: mainQuery.data.openCount, closed: mainQuery.data.closedCount}

  return {
    contentQueryKey: mainQuery.queryKey,
    error: mainQuery.data.error,
    isDeferredLoading: mainResults.length > 0 && deferredQuery.isPending,
    pageInfo: {
      currentPage: mainQuery.data?.currentPage ?? 1,
      totalPages: mainQuery.data?.totalPages ?? 0,
      totalCount: mainQuery.data?.totalCount ?? 0,
    },
    pulls,
    refetch: mainQuery.refetch,
    stateCounts,
  }
}
